import { useState } from "react";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { site } from "@/data/site";
import { track } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { sendContactMessage } from "@/lib/contact.functions";

const schema = z.object({
  name: z.string().trim().min(1, { message: "Please tell Paul your name" }).max(100, {
    message: "Name must be under 100 characters",
  }),
  email: z
    .string()
    .trim()
    .email({ message: "Enter a valid email address" })
    .max(255, { message: "Email must be under 255 characters" }),
  phone: z.string().trim().max(40, { message: "Phone must be under 40 characters" }).optional(),
  subject: z.string().trim().max(120, { message: "Subject must be under 120 characters" }).optional(),
  message: z
    .string()
    .trim()
    .min(10, { message: "A sentence or two helps Paul understand the idea" })
    .max(2000, { message: "Message must be under 2000 characters" }),
});

type Values = z.infer<typeof schema>;
type Errors = Partial<Record<keyof Values, string>>;

const field =
  "mt-2 w-full rounded-md border border-ink/15 bg-background px-4 py-3 text-sm text-ink outline-none transition focus:border-ink/40 focus:ring-2 focus:ring-ink/10";

export function ContactForm() {
  const send = useServerFn(sendContactMessage);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const parsed = schema.safeParse({
      name: String(form.get("name") ?? ""),
      email: String(form.get("email") ?? ""),
      phone: String(form.get("phone") ?? ""),
      subject: String(form.get("subject") ?? ""),
      message: String(form.get("message") ?? ""),
    });

    if (!parsed.success) {
      const next: Errors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof Values;
        if (!next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }

    setErrors({});
    setStatus("sending");

    try {
      const result = await send({ data: parsed.data });
      if (!result.ok) {
        setStatus("error");
        return;
      }
      track("submit_contact_form", { location: "contact_page" });
      setStatus("sent");
      formEl.reset();
    } catch {
      setStatus("error");
    }
  }

  if (status === "sent") {
    return (
      <div className="surface-card p-8">
        <p className="eyebrow">Message sent</p>
        <p className="mt-3 font-[family-name:var(--font-display)] text-2xl">
          Your message is with Paul.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          A copy is on its way to your inbox. Paul answers every message himself, usually within a day
          or two.
        </p>
        <Button className="mt-6 h-11 px-6" onClick={() => setStatus("idle")}>
          Send another message
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="surface-card p-8">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-name" className="text-sm font-semibold">
            Your name
          </label>
          <input id="cf-name" name="name" autoComplete="name" maxLength={100} className={field} />
          {errors.name && <p className="mt-1.5 text-xs text-destructive">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="cf-email" className="text-sm font-semibold">
            Email address
          </label>
          <input id="cf-email" name="email" type="email" autoComplete="email" maxLength={255} className={field} />
          {errors.email && <p className="mt-1.5 text-xs text-destructive">{errors.email}</p>}
        </div>
        <div>
          <label htmlFor="cf-phone" className="text-sm font-semibold">
            Phone <span className="font-normal text-muted-foreground">(optional)</span>
          </label>
          <input id="cf-phone" name="phone" type="tel" autoComplete="tel" maxLength={40} className={field} />
          {errors.phone && <p className="mt-1.5 text-xs text-destructive">{errors.phone}</p>}
        </div>
        <div>
          <label htmlFor="cf-subject" className="text-sm font-semibold">
            Subject <span className="font-normal text-muted-foreground">(optional)</span>
          </label>
          <input id="cf-subject" name="subject" maxLength={120} className={field} />
          {errors.subject && <p className="mt-1.5 text-xs text-destructive">{errors.subject}</p>}
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="cf-message" className="text-sm font-semibold">
          What are you looking for?
        </label>
        <textarea
          id="cf-message"
          name="message"
          rows={6}
          maxLength={2000}
          placeholder="Tell Paul about the piece, who it is for, any wording, and when you need it."
          className={field}
        />
        {errors.message && <p className="mt-1.5 text-xs text-destructive">{errors.message}</p>}
      </div>

      <div className="mt-7 flex flex-wrap items-center gap-4">
        <Button type="submit" className="h-12 px-7" disabled={status === "sending"}>
          {status === "sending" ? "Sending…" : "Send to Paul"}
        </Button>
        <p className="text-xs text-muted-foreground">
          Paul receives your message straight away, and you get a copy by email.
        </p>
      </div>

      {status === "error" && (
        <p className="mt-5 border-t border-hairline pt-4 text-sm text-destructive">
          Something went wrong sending your message. Please email {site.email} directly and Paul will
          pick it up.
        </p>
      )}
    </form>
  );
}
